import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { supabase } from '../utils/supabase/client';
import { projectId, publicAnonKey } from '../utils/supabase/info';
import { User } from '../types';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (userData: SignUpData) => Promise<{ needsVerification: boolean; message: string }>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<User>) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  uploadAvatar: (file: string, fileName: string) => Promise<string>;
  accessToken: string | null;
  getValidToken: () => Promise<string>;
}

interface SignUpData {
  email: string;
  password: string;
  name: string;
  idNumber: string;
  role: 'student' | 'faculty';
  department?: string;
  yearLevel?: number;
  course?: string;
  position?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  // useRef so the flag is mutable and visible across all closures (not stale like useState)
  const isSigningInRef = useRef(false);
  const isInitializingRef = useRef(false);

  const serverUrl = `https://${projectId}.supabase.co/functions/v1/make-server-0b38861b`;

  const buildAuthHeaders = (token: string): HeadersInit => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  });

  const refreshAndGetToken = async () => {
    const { data, error } = await supabase.auth.refreshSession();
    if (error || !data.session?.access_token) {
      return null;
    }
    setAccessToken(data.session.access_token);
    return data.session.access_token;
  };

  // Fetch user profile from server
  const fetchUserProfile = async (token: string) => {
    if (!token) {
      setUser(null);
      setAccessToken(null);
      return;
    }

    try {
      let response = await fetch(`${serverUrl}/user/profile`, {
        headers: buildAuthHeaders(token),
      });

      if (response.status === 401) {
        const refreshedToken = await refreshAndGetToken();
        if (refreshedToken) {
          response = await fetch(`${serverUrl}/user/profile`, {
            headers: buildAuthHeaders(refreshedToken),
          });
        }
      }

      if (response.ok) {
        const data = await response.json();

        // CLIENT-SIDE VERIFICATION GATE (defense-in-depth)
        // Even if the server returns user data, block unverified non-admin users
        if (data.user && data.user.isVerified === false && data.user.role !== 'administrator') {
          console.error('Client-side check: account not verified, signing out');
          await supabase.auth.signOut();
          setUser(null);
          setAccessToken(null);
          throw new Error('ACCOUNT_NOT_VERIFIED');
        }

        setUser(data.user);
        return;
      }

      // Handle non-ok responses
      let errorCode = '';
      try {
        const errorData = await response.json();
        errorCode = errorData.code || '';
      } catch {
        // ignore JSON parse errors
      }
      
      if (response.status === 403 && errorCode === 'ACCOUNT_NOT_VERIFIED') {
        await supabase.auth.signOut();
        setUser(null);
        setAccessToken(null);
        throw new Error('ACCOUNT_NOT_VERIFIED');
      } else if (response.status === 403 && errorCode === 'ACCOUNT_DELETED') {
        await supabase.auth.signOut();
        setUser(null);
        setAccessToken(null);
        throw new Error('ACCOUNT_DELETED');
      } else if (response.status === 401) {
        await supabase.auth.signOut();
      }
      
      setUser(null);
    } catch (error) {
      // Re-throw ACCOUNT_NOT_VERIFIED / ACCOUNT_DELETED so signIn() can catch it and show the message
      if (error instanceof Error && (error.message === 'ACCOUNT_NOT_VERIFIED' || error.message === 'ACCOUNT_DELETED')) {
        throw error;
      }
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        console.error('Network error: Unable to connect to server.');
      } else {
        console.error('Error fetching user profile:', error);
      }
      setUser(null);
    }
  };

  // Check for existing session on mount
  useEffect(() => {
    const initAuth = async () => {
      isInitializingRef.current = true;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.access_token) {
          // Proactively refresh the session to get a fresh access token.
          // getSession() returns the locally cached token which may be expired,
          // causing a 401 from the Supabase gateway before the function code runs.
          const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();

          if (refreshError || !refreshData?.session?.access_token) {
            // Refresh token is expired/invalid — session is irrecoverable.
            // Sign out to clear stale local storage so the user can log in fresh.
            console.warn('Session refresh failed, signing out:', refreshError?.message);
            await supabase.auth.signOut();
            setAccessToken(null);
            setUser(null);
            return;
          }

          const freshToken = refreshData.session.access_token;
          setAccessToken(freshToken);
          try {
            await fetchUserProfile(freshToken);
          } catch {
            // If profile fetch fails (e.g., account not verified), clear everything
            setAccessToken(null);
            setUser(null);
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        isInitializingRef.current = false;
        setLoading(false);
      }
    };

    initAuth();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Skip if signIn() or initAuth() is actively handling auth — they manage state themselves
      // Using ref instead of state so this closure always sees the current value
      if (isSigningInRef.current || isInitializingRef.current) {
        return;
      }

      // initAuth() already handles the initial profile load.
      // Skipping this avoids duplicate startup requests that can surface avoidable 401 noise.
      if (event === 'INITIAL_SESSION') {
        return;
      }

      if (event === 'SIGNED_OUT') {
        setAccessToken(null);
        setUser(null);
        setLoading(false);
        return;
      }

      if (session?.access_token) {
        setAccessToken(session.access_token);
        try {
          await fetchUserProfile(session.access_token);
        } catch {
          setAccessToken(null);
          setUser(null);
        }
      } else {
        setAccessToken(null);
        setUser(null);
      }
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    // Set ref BEFORE the async call so the listener sees it immediately
    isSigningInRef.current = true;
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });

      if (error) {
        throw error;
      }

      if (data.session?.access_token) {
        setAccessToken(data.session.access_token);
        try {
          await fetchUserProfile(data.session.access_token);
        } catch (profileError) {
          if (profileError instanceof Error && profileError.message === 'ACCOUNT_NOT_VERIFIED') {
            setAccessToken(null);
            throw new Error('Your account is pending administrator verification. You will receive an email notification once your account has been approved.');
          }
          if (profileError instanceof Error && profileError.message === 'ACCOUNT_DELETED') {
            setAccessToken(null);
            throw new Error('Your account has been removed by an administrator. Please register again if you wish to use the system.');
          }
          throw profileError;
        }
      }
    } catch (error) {
      throw error;
    } finally {
      isSigningInRef.current = false;
    }
  };



  const signUp = async (userData: SignUpData): Promise<{ needsVerification: boolean; message: string }> => {
    try {
      const response = await fetch(`${serverUrl}/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${publicAnonKey}`,
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to sign up');
      }

      // Do NOT auto-login — user must wait for admin verification
      return {
        needsVerification: data.needsVerification ?? true,
        message: data.message || 'Registration successful! Your account is pending administrator verification.',
      };
    } catch (error) {
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Unable to connect to server. Please ensure the Supabase Edge Function is deployed.');
      }
      console.error('Sign up error:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setAccessToken(null);
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      if (!user?.email) {
        throw new Error('Not authenticated');
      }

      // Verify the current password by attempting to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword,
      });

      if (signInError) {
        throw new Error('Current password is incorrect');
      }

      // Update to the new password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        throw new Error(updateError.message || 'Failed to update password');
      }
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  };

  const updateProfile = async (updates: Partial<User>) => {
    try {
      if (!accessToken) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${serverUrl}/user/profile`, {
        method: 'PUT',
        headers: buildAuthHeaders(accessToken),
        body: JSON.stringify(updates),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update profile');
      }

      setUser(data.user);
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  };

  const uploadAvatar = async (file: string, fileName: string): Promise<string> => {
    try {
      if (!accessToken) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${serverUrl}/user/avatar`, {
        method: 'POST',
        headers: buildAuthHeaders(accessToken),
        body: JSON.stringify({ file, fileName }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to upload avatar');
      }

      if (data.user) {
        setUser(data.user);
      }

      return data.avatarUrl;
    } catch (error) {
      console.error('Upload avatar error:', error);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    changePassword,
    uploadAvatar,
    accessToken,
    getValidToken: async () => {
      if (accessToken) {
        // Try to use the current token first; if it's still valid Supabase won't
        // make a network call.  refreshSession will return a fresh token if expired.
        const { data, error } = await supabase.auth.refreshSession();
        if (!error && data.session?.access_token) {
          setAccessToken(data.session.access_token);
          return data.session.access_token;
        }
      }
      throw new Error('Not authenticated');
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
